import Vue from 'vue';
import { ref } from '@vue/composition-api';
import { message } from '@utils/wuji-message';
import { cloneDeep, defaultsDeep } from 'lodash';
import wujiFetch from '@utils/wujiFetch';
import { BASE_API_PATH, getDesignEnvPath } from '@/config/constant';
import { getDefaultProjectSettings, getDefaultPublishHookRobotMsg } from '@loaders/project/utils';
import { useStore } from '@/store';
import { ConfirmModal } from '@components/confirmModal/ConfirmModal';
export const RELEASE_APPROVAL_ID = 'general-release-approval';
const versionUrl = `${BASE_API_PATH}/xy/design/env/dev/version`;
export const fetchPublishSetting = async (projectId) => {
    const store = useStore();
    const data = await store.dispatch('fetchProjectSettings', {
        projectId,
    });
    return cloneDeep(data);
};
export const fetchRuntimePermissionSetting = async (projectId) => {
    const store = useStore();
    const data = await store.dispatch('fetchRuntimePermissionSettings', {
        projectId,
    });
    return cloneDeep(data);
};
export default function useProjectSetting({ projectId }) {
    const store = useStore();
    const projectSetting = ref(getDefaultProjectSettings());
    const baseProjectSetting = ref(getDefaultProjectSettings());
    const customWorkflowConfig = ref(null);
    const updating = ref(false);
    const initLoading = ref(false);
    const fetchProjectSetting = async () => {
        try {
            const data = await fetchPublishSetting(projectId.value);
            const defaultSetting = getDefaultProjectSettings();
            if (data) {
                projectSetting.value = defaultsDeep(data, defaultSetting);
                if (!projectSetting.value.robots)
                    projectSetting.value.robots = [];
                baseProjectSetting.value = cloneDeep(projectSetting.value);
                if (!projectSetting.value.versionSetting.enablePublishHook
                    && !projectSetting.value.versionSetting.publishHookSetting.value.data) {
                    projectSetting.value.versionSetting.publishHookSetting.value.data = getDefaultPublishHookRobotMsg();
                }
            }
            if (!projectSetting.value.branchSetting)
                projectSetting.value.branchSetting = defaultSetting.branchSetting;
            return projectSetting.value;
        }
        catch (err) {
            message.error(`获取应用设置失败:${err.message}`);
        }
    };
    /**
     * @param muted 为 true 时不弹 toast，并将错误继续抛出（供 workbench FormObject / FormCallResult 统一提示）
     */
    const saveProjectSetting = async (data, { muted = false } = {}) => {
        try {
            updating.value = true;
            const updateData = await store.dispatch('saveProjectSettings', {
                projectId: projectId.value,
                data,
            });
            if (!muted)
                message.success('保存成功');
            baseProjectSetting.value = cloneDeep(data);
            return updateData;
        }
        catch (err) {
            if (!muted)
                message.error(`保存失败:${err.message}`);
            // workbench biz 依赖异常判定失败；旧页面依赖返回值 falsy，故仅 muted 时抛出
            if (muted)
                throw err;
        }
        finally {
            updating.value = false;
        }
    };
    const confirmSaveProjectSetting = async () => {
        if (JSON.stringify(projectSetting.value) !== JSON.stringify(baseProjectSetting.value)) {
            return await new Promise((rs) => {
                const instance = new Vue({
                    render: h => h(ConfirmModal, {
                        on: {
                            close: (isSave) => {
                                rs(isSave);
                                instance.$destroy();
                                instance.$el?.parentNode?.removeChild(instance.$el);
                            },
                        },
                    }),
                });
                const el = document.createElement('div');
                document.body.appendChild(el);
                instance.$mount();
            });
        }
        return false;
    };
    const fetchCustomWorkflow = async () => {
        const path = `${getDesignEnvPath()}/manage/workflow/${RELEASE_APPROVAL_ID}?projectid=${projectId.value}`;
        const flowConfig = await wujiFetch(path);
        customWorkflowConfig.value = flowConfig;
    };
    const initApprovalWorkflow = async () => {
        const flowConfig = await wujiFetch(`${versionUrl}/project/approval/init?projectid=${projectId.value}`, {
            method: 'POST',
        });
        return flowConfig;
    };
    const initCustomWorkflow = async () => {
        initLoading.value = true;
        try {
            const flowConfig = await initApprovalWorkflow();
            customWorkflowConfig.value = flowConfig;
            message.success('生成发版审批流程成功');
        }
        catch (error) {
            message.error('生成发版审批流程失败');
        }
        initLoading.value = false;
    };
    return {
        fetchProjectSetting,
        fetchCustomWorkflow,
        initCustomWorkflow,
        saveProjectSetting,
        confirmSaveProjectSetting,
        projectSetting,
        customWorkflowConfig,
        updating,
    };
}
